import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, Building2, Layers, LayoutDashboard } from 'lucide-react';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';

const StageForm = ({ stage, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    stageId: '',
    stageName: '',
    probability: 0,
    type: 'active',
    isActive: true,
    activityGoal: '',
    exitCriteria: '',
    color: '#3B82F6',
    orderSequence: 1,
    divisionIds: [],
    verticalIds: [],
    dashboardConfig: {
      showLeadCount: true,
      showRevenue: true,
      showPercentage: true,
      showQuotation: false,
    },
  });

  const [divisions, setDivisions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const predefinedColors = [
    '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
    '#10B981', '#059669', '#DC2626', '#6B7280'
  ];

  // Fetch divisions and business verticals
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [divRes, prodRes] = await Promise.all([
          api.get(SummaryApi.getAllDivisions?.url),
          api.get(SummaryApi.getAllProducts?.url),
        ]);
        if (divRes.data?.success) setDivisions(divRes.data.data || []);
        if (prodRes.data?.success) setVerticals(prodRes.data.data || []);
      } catch (err) {
        console.error('Failed to load form dependencies:', err);
      }
    };
    fetchDropdownData();
  }, []);

  // Populate data on edit mode
  useEffect(() => {
    if (stage) {
      setFormData({
        stageId: stage.stageId || '',
        stageName: stage.stageName || '',
        probability: stage.probability || 0,
        type: stage.type || 'active',
        isActive: stage.isActive ?? true,
        activityGoal: stage.activityGoal || '',
        exitCriteria: stage.exitCriteria || '',
        color: stage.color || '#3B82F6',
        orderSequence: stage.orderSequence || 1,
        divisionIds: stage.divisionIds?.map(d => d._id || d) || [],
        verticalIds: stage.verticalIds?.map(v => v._id || v) || [],
        dashboardConfig: {
          showLeadCount: stage.dashboardConfig?.showLeadCount ?? true,
          showRevenue: stage.dashboardConfig?.showRevenue ?? true,
          showPercentage: stage.dashboardConfig?.showPercentage ?? true,
          showQuotation: stage.dashboardConfig?.showQuotation ?? false,
        },
      });
    }
  }, [stage]);

  // Filter available verticals based on selected divisions
  const availableVerticals = useMemo(() => {
    if (formData.divisionIds.length === 0) return verticals;
    return verticals.filter(item => {
      const divId = item.divisionId?._id || item.divisionId;
      return formData.divisionIds.includes(divId);
    });
  }, [verticals, formData.divisionIds]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.stageId.trim()) newErrors.stageId = 'Stage ID is required';
    if (!formData.stageName.trim()) newErrors.stageName = 'Stage name is required';
    if (formData.orderSequence <= 0) newErrors.orderSequence = 'Order Sequence must be greater than 0';
    if (formData.probability < 0 || formData.probability > 100) {
      newErrors.probability = 'Probability must be between 0 and 100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const url = stage
        ? (typeof SummaryApi.updateStage.url === 'function' ? SummaryApi.updateStage.url(stage._id) : SummaryApi.updateStage.url)
        : SummaryApi.createStage.url;

      const method = stage ? api.put : api.post;
      await method(url, formData);

      onSuccess();
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || 'Failed to save stage' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['probability', 'orderSequence'].includes(name) ? parseInt(value) || 0 : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDivisionChange = (divisionId) => {
    setFormData(prev => {
      const newDivisionIds = prev.divisionIds.includes(divisionId)
        ? prev.divisionIds.filter(id => id !== divisionId)
        : [...prev.divisionIds, divisionId];
      return { ...prev, divisionIds: newDivisionIds, verticalIds: [] };
    });
  };

  const handleVerticalChange = (verticalId) => {
    setFormData(prev => {
      const newVerticalIds = prev.verticalIds.includes(verticalId)
        ? prev.verticalIds.filter(id => id !== verticalId)
        : [...prev.verticalIds, verticalId];
      return { ...prev, verticalIds: newVerticalIds };
    });
  };

  const handleDashboardConfigChange = (field) => {
    setFormData(prev => ({
      ...prev,
      dashboardConfig: {
        ...prev.dashboardConfig,
        [field]: !prev.dashboardConfig[field]
      }
    }));
  };

  return (
    <>
      <div className="fixed inset-0 top-[57px] md:top-[65px] bg-black/50 backdrop-blur-xs transition-opacity duration-300 z-50" onClick={onClose} />
      <div className="fixed top-[57px] md:top-[65px] right-0 h-full w-full max-w-3xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
        <div className="h-full bg-white flex flex-col justify-between overflow-hidden">
          
          {/* Header */}
          <div className="bg-white border-b px-6 py-4 flex items-center justify-between shrink-0">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900">{stage ? 'Edit Stage' : 'Add New Stage'}</h2>
              <p className="text-xs text-gray-500">Configure stage workflow, pipeline visibility, and target criteria</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Scroll Body */}
          <form id="stage-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start text-sm">
                <AlertCircle className="h-5 w-5 mr-2 shrink-0 mt-0.5" />
                <span>{errors.submit}</span>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Stage ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="stageId"
                  value={formData.stageId}
                  onChange={handleChange}
                  disabled={stage && stage.statistics?.count > 0}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.stageId ? 'border-red-500' : 'border-gray-300'} ${stage && stage.statistics?.count > 0 ? 'bg-gray-100' : ''}`}
                  placeholder="e.g., S1, S2_QUAL"
                />
                {errors.stageId && <p className="mt-1 text-xs text-red-600">{errors.stageId}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Order Sequence <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="orderSequence"
                  value={formData.orderSequence}
                  onChange={handleChange}
                  min="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.orderSequence ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.orderSequence && <p className="mt-1 text-xs text-red-600">{errors.orderSequence}</p>}
              </div>
            </div>

            {/* Stage Name & Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Stage Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="stageName"
                  value={formData.stageName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.stageName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="e.g., Prospecting / Qualified"
                />
                {errors.stageName && <p className="mt-1 text-xs text-red-600">{errors.stageName}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Status</label>
                <select
                  name="isActive"
                  value={formData.isActive ? "true" : "false"}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* Division & Vertical Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Divisions Assignment */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                <label className="flex items-center text-xs font-semibold text-gray-700 uppercase mb-2">
                  <Building2 className="h-4 w-4 mr-1 text-gray-500" />
                  Assign Divisions
                </label>
                <div className="space-y-1.5 max-h-36 overflow-y-auto text-xs bg-white border border-gray-200 rounded-md p-2">
                  <label className="flex items-center space-x-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.divisionIds.length === 0}
                      onChange={() => setFormData(prev => ({ ...prev, divisionIds: [], verticalIds: [] }))}
                      className="rounded text-red-600 focus:ring-red-500"
                    />
                    <span className="font-medium text-gray-700">All Divisions (Shared)</span>
                  </label>
                  {divisions.map(division => (
                    <label key={division._id} className="flex items-center space-x-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.divisionIds.includes(division._id)}
                        onChange={() => handleDivisionChange(division._id)}
                        className="rounded text-red-600 focus:ring-red-500"
                      />
                      <span className="text-gray-800">{division.name || division.divisionName}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Business Verticals Assignment */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                <label className="flex items-center text-xs font-semibold text-gray-700 uppercase mb-2">
                  <Layers className="h-4 w-4 mr-1 text-gray-500" />
                  Assign Verticals
                </label>
                <div className="space-y-1.5 max-h-36 overflow-y-auto text-xs bg-white border border-gray-200 rounded-md p-2">
                  <label className="flex items-center space-x-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.verticalIds.length === 0}
                      onChange={() => setFormData(prev => ({ ...prev, verticalIds: [] }))}
                      className="rounded text-red-600 focus:ring-red-500"
                    />
                    <span className="font-medium text-gray-700">All Verticals (Shared)</span>
                  </label>
                  {availableVerticals.map(vertical => (
                    <label key={vertical._id} className="flex items-center space-x-2 p-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.verticalIds.includes(vertical._id)}
                        onChange={() => handleVerticalChange(vertical._id)}
                        className="rounded text-red-600 focus:ring-red-500"
                      />
                      <span className="text-gray-800 truncate">{vertical.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Dashboard Visibility Cards Setting */}
            <div className="border border-gray-200 rounded-lg p-3.5 bg-gray-50/40">
              <div className="flex items-center gap-1.5 mb-2.5">
                <LayoutDashboard className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-gray-800 uppercase">Dashboard & Kanban Card Settings</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: 'showLeadCount', label: 'Lead Count', desc: 'Display total leads' },
                  { key: 'showRevenue', label: 'Revenue Value', desc: 'Display deal value' },
                  { key: 'showPercentage',  label: 'Stage Share %', desc: '% of total leads in this stage' },
                  { key: 'showQuotation', label: 'Quotations', desc: 'Display quote count' },
                ].map(({ key, label, desc }) => (
                  <div
                    key={key}
                    onClick={() => handleDashboardConfigChange(key)}
                    className={`flex flex-col justify-between p-2.5 rounded-lg border text-left cursor-pointer transition-all select-none ${
                      formData.dashboardConfig[key]
                        ? 'bg-red-50/70 border-red-300 text-gray-900 shadow-xs'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold leading-tight">{label}</span>
                      <input
                        type="checkbox"
                        checked={formData.dashboardConfig[key]}
                        onChange={() => {}}
                        className="rounded text-red-600 focus:ring-red-500 h-3.5 w-3.5 pointer-events-none"
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 mt-1">{desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Probability & Stage Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
                  Probability (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="probability"
                  value={formData.probability}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${errors.probability ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.probability && <p className="mt-1 text-xs text-red-600">{errors.probability}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Stage Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                >
                  <option value="active">Active</option>
                  <option value="won">Won (Final Stage)</option>
                  <option value="lost">Lost (Final Stage)</option>
                </select>
              </div>
            </div>

            {/* Goals & Exit Criteria */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Activity Goal</label>
                <textarea
                  name="activityGoal"
                  value={formData.activityGoal}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  placeholder="Primary objective in this stage..."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">Exit Criteria</label>
                <textarea
                  name="exitCriteria"
                  value={formData.exitCriteria}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  placeholder="Requirements to advance to next stage..."
                />
              </div>
            </div>

            {/* Stage Color */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase mb-1.5">Stage Color</label>
              <div className="flex flex-wrap gap-2.5">
                {predefinedColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg border-2 transition-all ${
                      formData.color === color ? 'border-gray-900 scale-110 shadow-sm' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Select color ${color}`}
                  />
                ))}
              </div>
            </div>
          </form>

          {/* Sticky Bottom Actions */}
          <div className="sticky bottom-0 bg-white border-t -mx-6 px-6 py-4 mt-8 mb-16">
            <div className="flex justify-end gap-3 px-3 mb-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="stage-form"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 text-xs md:text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Saving...' : (stage ? 'Update Stage' : 'Create Stage')}</span>
            </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StageForm;


// components/stages/StageForm.jsx
// import React, { useState, useEffect } from 'react';
// import { X, Save, AlertCircle, Building2 } from 'lucide-react';
// import SummaryApi from '../../common/SummaryApi';
// import api from '../../common/apiClient';

// const StageForm = ({ stage, onClose, onSuccess }) => {
//   const [formData, setFormData] = useState({
//     stageId: '',
//     stageName: '',
//     probability: 0,
//     type: 'active',
//     isActive: true,
//     activityGoal: '',
//     exitCriteria: '',
//     color: '#DC2626',
//     stageOrder: 1,
//     orderSequence: 1,
//     divisionIds: [] // Add divisionIds
//   });
//   const [divisions, setDivisions] = useState([]);
//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);

//   const predefinedColors = [
//     '#DC2626', '#EF4444', '#B91C1C', '#3B82F6',
//     '#8B5CF6', '#10B981', '#F59E0B', '#6B7280'
//   ];

//   // Fetch divisions
//   useEffect(() => {
//     const fetchDivisions = async () => {
//       try {
//         const res = await api.get(SummaryApi.getAllDivisions.url);
//         if (res.data.success) {
//           setDivisions(res.data.data);
//         }
//       } catch (err) {
//         console.error("Failed to fetch divisions:", err);
//       }
//     };
//     fetchDivisions();
//   }, []);

//   useEffect(() => {
//     if (stage) {
//       setFormData({
//         stageId: stage.stageId || '',
//         stageName: stage.stageName || '',
//         probability: stage.probability || 0,
//         type: stage.type || 'active',
//         isActive: stage.isActive ?? true,
//         activityGoal: stage.activityGoal || '',
//         exitCriteria: stage.exitCriteria || '',
//         color: stage.color || '#DC2626',
//         stageOrder: stage.stageOrder || 1,
//         orderSequence: stage.orderSequence || 1,
//         divisionIds: stage.divisionIds?.map(d => d._id || d) || []
//       });
//     }
//   }, [stage]);

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.stageId.trim()) {
//       newErrors.stageId = 'Stage ID is required';
//     }
//     if (!formData.stageName.trim()) {
//       newErrors.stageName = 'Stage name is required';
//     }
//     if (formData.orderSequence <= 0) {
//       newErrors.orderSequence =
//         "Order Sequence must be greater than 0";
//     }
//     if (formData.probability < 0 || formData.probability > 100) {
//       newErrors.probability = 'Probability must be between 0 and 100';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     if (!validateForm()) return;

//     setLoading(true);
//     try {
//       if (stage) {
//         await api.put(SummaryApi.updateStage.url(stage._id), formData);
//       } else {
//         await api.post(SummaryApi.createStage.url, formData);
//       }
//       onSuccess();
//     } catch (err) {
//       setErrors({ 
//         submit: err.response?.data?.message || 'Failed to save stage' 
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]:["probability", "stageOrder", "orderSequence"].includes(name)
//       ? parseInt(value) || 0
//       : value
//     }));
    
//     if (errors[name]) {
//       setErrors(prev => ({ ...prev, [name]: '' }));
//     }
//   };

//   const handleDivisionChange = (divisionId) => {
//     setFormData(prev => {
//       const newDivisionIds = prev.divisionIds.includes(divisionId)
//         ? prev.divisionIds.filter(id => id !== divisionId)
//         : [...prev.divisionIds, divisionId];
//       return { ...prev, divisionIds: newDivisionIds };
//     });
//   };

//   return (
//     <>
//       <div 
//         className="fixed inset-0 top-[73px] md:top-[81px] bg-black bg-opacity-50 transition-opacity duration-300 z-50"
//         onClick={onClose}
//       />
      
//       <div className="fixed top-[73px] md:top-[81px] right-0 h-full w-full max-w-3xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
//         <div className="h-full bg-white shadow-xl overflow-y-auto">
//           {/* Header */}
//           <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
//             <h2 className="text-xl font-semibold text-gray-900">
//               {stage ? 'Edit Stage' : 'Add New Stage'}
//             </h2>
//             <button
//               onClick={onClose}
//               className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
//             >
//               <X className="h-5 w-5 text-gray-500" />
//             </button>
//           </div>

//           <form onSubmit={handleSubmit} className="p-6">
//             {errors.submit && (
//               <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
//                 <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
//                 <span>{errors.submit}</span>
//               </div>
//             )}
//             <div className="grid grid-cols-2 gap-4 mb-4">
//             {/* Basic Fields */}
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Stage ID <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 name="stageId"
//                 value={formData.stageId}
//                 onChange={handleChange}
//                 disabled={stage && stage.statistics?.count > 0}
//                 className={`
//                   w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500
//                   ${errors.stageId ? 'border-red-500' : 'border-gray-300'}
//                   ${stage && stage.statistics?.count > 0 ? 'bg-gray-100' : ''}
//                 `}
//                 placeholder="e.g., S1, S2"
//               />
//               {errors.stageId && (
//                 <p className="mt-1 text-sm text-red-600">{errors.stageId}</p>
//               )}
//             </div>
//             <div>
//               <label>Order Sequence</label>
//                 <input
//                   type="number"
//                   name="orderSequence"
//                   value={formData.orderSequence}
//                   onChange={handleChange}
//                   min="0"
//                   max="100"
//                   className={`
//                     w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500
//                     ${errors.orderSequence ? 'border-red-500' : 'border-gray-300'}
//                   `}
//                 />
//                 {errors.orderSequence && (
//                   <p className="mt-1 text-sm text-red-600">{errors.orderSequence}</p>
//                 )}
//             </div>
//             </div>
//             {/* Division Selection */}
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 <Building2 className="inline h-4 w-4 mr-1" />
//                 Assign to Divisions
//               </label>
//               <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
//                 <label className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded cursor-pointer">
//                   <input
//                     type="checkbox"
//                     checked={formData.divisionIds.length === 0}
//                     onChange={() => setFormData(prev => ({ ...prev, divisionIds: [] }))}
//                     className="rounded text-red-600 focus:ring-red-500"
//                   />
//                   <span className="text-sm">All Divisions (Shared)</span>
//                 </label>
//                 {divisions.map(division => (
//                   <label
//                     key={division._id}
//                     className="flex items-center space-x-2 hover:bg-gray-50 p-2 rounded cursor-pointer"
//                   >
//                     <input
//                       type="checkbox"
//                       checked={formData.divisionIds.includes(division._id)}
//                       onChange={() => handleDivisionChange(division._id)}
//                       className="rounded text-red-600 focus:ring-red-500"
//                     />
//                     <span className="text-sm">
//                       {division.name} ({division.divisionId})
//                     </span>
//                   </label>
//                 ))}
//               </div>
//               <p className="mt-1 text-xs text-gray-500">
//                 Leave unchecked to share this stage across all divisions
//               </p>
//             </div>

//             {/* Rest of the form fields... */}
//             <div className="grid grid-cols-2 gap-4 mb-4">

//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Stage Name <span className="text-red-500">*</span>
//               </label>
//               <input
//                 type="text"
//                 name="stageName"
//                 value={formData.stageName}
//                 onChange={handleChange}
//                 className={`
//                   w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500
//                   ${errors.stageName ? 'border-red-500' : 'border-gray-300'}
//                 `}
//                 placeholder="e.g., Prospecting"
//               />
//               {errors.stageName && (
//                 <p className="mt-1 text-sm text-red-600">{errors.stageName}</p>
//               )}
//             </div>
//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Status
//               </label>

//               <select
//                 name="isActive"
//                 value={formData.isActive ? "true" : "false"}
//                 onChange={(e) =>
//                   setFormData((prev) => ({
//                     ...prev,
//                     isActive: e.target.value === "true",
//                   }))
//                 }
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg"
//               >
//                 <option value="true">Active</option>
//                 <option value="false">Inactive</option>
//               </select>
//             </div>
//             </div>
//             <div className="grid grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Probability (%) <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="number"
//                   name="probability"
//                   value={formData.probability}
//                   onChange={handleChange}
//                   min="0"
//                   max="100"
//                   className={`
//                     w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500
//                     ${errors.probability ? 'border-red-500' : 'border-gray-300'}
//                   `}
//                 />
//                 {errors.probability && (
//                   <p className="mt-1 text-sm text-red-600">{errors.probability}</p>
//                 )}
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Stage Type
//                 </label>
//                 <select
//                   name="type"
//                   value={formData.type}
//                   onChange={handleChange}
//                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
//                 >
//                   <option value="active">Active</option>
//                   <option value="won">Won</option>
//                   <option value="lost">Lost</option>
//                 </select>
//               </div>
//             </div>

//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Activity Goal
//               </label>
//               <textarea
//                 name="activityGoal"
//                 value={formData.activityGoal}
//                 onChange={handleChange}
//                 rows="3"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
//                 placeholder="What should be accomplished in this stage?"
//               />
//             </div>

//             <div className="mb-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Exit Criteria
//               </label>
//               <textarea
//                 name="exitCriteria"
//                 value={formData.exitCriteria}
//                 onChange={handleChange}
//                 rows="3"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
//                 placeholder="What needs to happen to move to the next stage?"
//               />
//             </div>

//             <div className="mb-6">
//               <label className="block text-sm font-medium text-gray-700 mb-2">
//                 Stage Color
//               </label>
//               <div className="grid grid-cols-4 gap-2">
//                 {predefinedColors.map((color) => (
//                   <button
//                     key={color}
//                     type="button"
//                     onClick={() => setFormData(prev => ({ ...prev, color }))}
//                     className={`
//                       h-10 rounded-lg border-2 transition-all
//                       ${formData.color === color 
//                         ? 'border-gray-800 scale-110 shadow-md' 
//                         : 'border-transparent hover:scale-105'
//                       }
//                     `}
//                     style={{ backgroundColor: color }}
//                     aria-label={`Select color ${color}`}
//                   />
//                 ))}
//               </div>
//             </div>

//             <div className="sticky bottom-0 bg-white border-t -mx-6 px-6 py-4 mt-8 mb-16">
//               <div className="flex justify-end gap-3">
//                 <button
//                   type="button"
//                   onClick={onClose}
//                   className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   disabled={loading}
//                   className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//                 >
//                   <Save className="h-5 w-5" />
//                   {loading ? 'Saving...' : (stage ? 'Update Stage' : 'Create Stage')}
//                 </button>
//               </div>
//             </div>
//           </form>
//         </div>
//       </div>
//     </>
//   );
// };

// export default StageForm;
